import { memo, useEffect, useState } from 'react'
import { Box, Divider, Grid, IconButton, Stack, TextField, Tooltip } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import Section from 'components/common/Section/Section'
import CustomText from 'components/common/CustomText/CustomText'
import CustomButton from 'components/buttons/CustomButton/CustomButton'
import CustomLabel from 'components/common/CustomLabel/CustomLabel'
import CustomDialog from 'components/dialogs/CustomDialog'
import MagicIcon from 'icons/MagicIcon'
import PencilIcon from 'icons/PencilIcon'
import SaveIcon from 'icons/SaveIcon'
import XIcon from 'icons/XIcon'
import { colors } from 'styles/colors'
import { useProductContext } from 'context/ProductContext'
import { useAnalyzeProductContents } from 'hooks/useAnalyzer'
import { useUpdateProductContents } from 'hooks/useProduct'
import { snackbarService } from 'services/snackbarService'

type Language = 'en' | 'ar';

interface ContentEntry {
  contentName: string;
  // Held as typed rather than as a number: an empty box is a pack that prints
  // no count, and a half-typed number is not one either.
  amount: string;
  amountUnit: string;
  analysis: string;
}

interface ContentsValue {
  introduction: { en: string; ar: string };
  entries: { en: ContentEntry; ar: ContentEntry }[];
}

const emptyEntry = (): ContentEntry => ({
  contentName: '',
  amount: '',
  amountUnit: '',
  analysis: '',
});

const emptyRow = () => ({ en: emptyEntry(), ar: emptyEntry() });

// Stored per language - an introduction and a list of what the pack holds - and
// the two lists are the same things named twice, so they are read here as rows
// with an English and an Arabic side rather than as two lists to keep in step.
const fromProduct = (contents: any): ContentsValue => {
  const en: any[] = contents?.en?.contents ?? [];
  const ar: any[] = contents?.ar?.contents ?? [];
  const length = Math.max(en.length, ar.length);

  const read = (entry: any): ContentEntry => ({
    contentName: entry?.contentName ?? '',
    amount: entry?.amount === null || entry?.amount === undefined ? '' : String(entry.amount),
    amountUnit: entry?.amountUnit ?? '',
    analysis: entry?.analysis ?? '',
  });

  return {
    introduction: {
      en: contents?.en?.introduction ?? '',
      ar: contents?.ar?.introduction ?? '',
    },
    entries: Array.from({ length }, (_value, index) => ({
      en: read(en[index]),
      ar: read(ar[index]),
    })),
  };
};

const toProduct = (value: ContentsValue) => {
  const write = (entry: ContentEntry) => ({
    contentName: entry.contentName.trim(),
    // Null rather than zero where nothing was typed: the column says the pack
    // prints no number, which is not the same as printing none of them.
    amount: entry.amount.trim() === '' ? null : Number(entry.amount),
    amountUnit: entry.amountUnit.trim(),
    analysis: entry.analysis.trim(),
  });

  // A row empty on both sides is one nobody wrote.
  const rows = value.entries.filter(
    (row) => row.en.contentName.trim() || row.ar.contentName.trim(),
  );

  return {
    en: {
      introduction: value.introduction.en.trim(),
      contents: rows.length ? rows.map((row) => write(row.en)) : null,
    },
    ar: {
      introduction: value.introduction.ar.trim(),
      contents: rows.length ? rows.map((row) => write(row.ar)) : null,
    },
  };
};

function ContentsDetails() {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const { product, refetch: refetchProduct } = useProductContext();
  const [value, setValue] = useState<ContentsValue>(fromProduct(null));
  const [removingRow, setRemovingRow] = useState<number | null>(null);
  // Folded away until asked for, like the other long sections on this page.
  const [isVisible, setIsVisible] = useState<boolean>(false);

  useEffect(() => {
    setValue(fromProduct(product.contents));
  }, [product.contents]);

  const { refetch, isFetching } = useAnalyzeProductContents(product.suggestion.id, {
    enabled: false,
    onSuccess: (res: any) => {
      setValue(fromProduct(res));
      setIsEditing(true);
    },
    onError: (error: any) => {
      console.log('error', error)
    },
    select: (res: any) => res.data,
  });

  const { mutate: updateMutation, isLoading } = useUpdateProductContents(
    () => {
      setIsEditing(false);
      snackbarService.showSnackbar('Product contents has been updated successfully', 'success');
      refetchProduct();
    },
    (err: any) => {
      snackbarService.showSnackbar('Could not update the contents', 'error');
    },
  )

  const setIntroduction = (language: Language, text: string) =>
    setValue((previous) => ({
      ...previous,
      introduction: { ...previous.introduction, [language]: text },
    }));

  const setField = (
    row: number,
    language: Language,
    field: keyof ContentEntry,
    text: string,
  ) =>
    setValue((previous) => ({
      ...previous,
      entries: previous.entries.map((entry, index) =>
        index === row
          ? { ...entry, [language]: { ...entry[language], [field]: text } }
          : entry,
      ),
    }));

  // Rows are added and removed on both sides at once: one thing out of the pack,
  // named in two languages.
  const addRow = () =>
    setValue((previous) => ({ ...previous, entries: [...previous.entries, emptyRow()] }));

  const removeRow = (row: number) =>
    setValue((previous) => ({
      ...previous,
      entries: previous.entries.filter((_entry, index) => index !== row),
    }));

  const handleSave = () => {
    updateMutation({ id: product.id, contents: toProduct(value) as any });
  }

  const handleCancel = () => {
    setValue(fromProduct(product.contents));
    setIsEditing(false);
  }

  const hasIntroduction = !!value.introduction.en.trim() || !!value.introduction.ar.trim();

  const entryFields = (row: number, language: Language) => {
    const entry = value.entries[row][language];
    const rtl = language === 'ar';

    return (
      <Stack gap={1.25}>
        <Box>
          <CustomLabel label={'Name'} />
          <TextField
            size="small"
            fullWidth
            value={entry.contentName}
            disabled={!isEditing}
            onChange={(event) => setField(row, language, 'contentName', event.target.value)}
            sx={rtl ? { '& input': { direction: 'rtl' } } : undefined}
          />
        </Box>

        <Stack direction="row" gap={1.25}>
          <Box sx={{ width: 110 }}>
            <CustomLabel label={'Amount'} />
            <TextField
              size="small"
              fullWidth
              type="number"
              value={entry.amount}
              disabled={!isEditing}
              onChange={(event) => setField(row, language, 'amount', event.target.value)}
            />
          </Box>

          <Box flex={1}>
            <CustomLabel label={'Unit'} />
            <TextField
              size="small"
              fullWidth
              value={entry.amountUnit}
              disabled={!isEditing}
              onChange={(event) => setField(row, language, 'amountUnit', event.target.value)}
              sx={rtl ? { '& input': { direction: 'rtl' } } : undefined}
            />
          </Box>
        </Stack>

        <Box>
          <CustomLabel label={'Analysis'} />
          <TextField
            size="small"
            fullWidth
            multiline
            minRows={3}
            value={entry.analysis}
            disabled={!isEditing}
            onChange={(event) => setField(row, language, 'analysis', event.target.value)}
            sx={rtl ? { '& textarea': { direction: 'rtl' } } : undefined}
          />
        </Box>
      </Stack>
    );
  };

  return (
    <Box mt={3}>
      <Section
        title="Product Contents"
        subtitle={value.entries.length ? `${value.entries.length} ${value.entries.length === 1 ? 'item' : 'items'}` : undefined}
        buttons={[
          ...(isEditing
            ? [
              {
                icon: <MagicIcon size={20} color={colors.primary} />,
                title: 'Analyze',
                onClick: () => refetch(),
                loading: isFetching,
                disabled: isLoading,
              },
              {
                icon: <SaveIcon size={20} color={colors.success} />,
                title: 'Save',
                onClick: () => handleSave(),
                loading: isLoading,
                disabled: isFetching,
              },
              {
                icon: <XIcon size={15} color={colors.error} />,
                title: 'Cancel',
                onClick: () => handleCancel(),
                disabled: isFetching || isLoading,
              },
            ]
            : [
              {
                icon: <PencilIcon size={20} color={colors.primary} />,
                title: 'Edit',
                onClick: () => {
                  setIsVisible(true);
                  setIsEditing(true);
                },
                disabled: isFetching || isLoading,
              }
            ]),
        ]}
        collapsed={!isVisible}
        onToggle={isEditing ? undefined : () => setIsVisible((prev) => !prev)}
        content={isVisible &&
          <Grid size={{ xs: 12 }}>
            {/* Empty boxes say nothing to a reader, so outside editing the
                introduction is only there when it has something in it. */}
            {(isEditing || hasIntroduction) && (
            <Grid container spacing={1.5}>
              <Grid size={{ xs: 12, md: 6 }}>
                <CustomLabel label={'English Introduction'} />
                <TextField
                  size="small"
                  fullWidth
                  multiline
                  minRows={3}
                  value={value.introduction.en}
                  disabled={!isEditing}
                  onChange={(event) => setIntroduction('en', event.target.value)}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <CustomLabel label={'Arabic Introduction'} />
                <TextField
                  size="small"
                  fullWidth
                  multiline
                  minRows={3}
                  value={value.introduction.ar}
                  disabled={!isEditing}
                  onChange={(event) => setIntroduction('ar', event.target.value)}
                  sx={{ '& textarea': { direction: 'rtl' } }}
                />
              </Grid>
            </Grid>
            )}

            {value.entries.length === 0 && !isEditing && (
              <Box mt={2}>
                <CustomText
                  text={'This pack holds nothing separate to name.'}
                  fontSize={14}
                  color={colors.gray}
                />
              </Box>
            )}

            <Stack gap={2.5} mt={2.5}>
              {value.entries.map((_entry, row) => (
                <Box
                  key={row}
                  sx={{
                    border: `1px solid ${colors.lightgray || '#e0e0e0'}`,
                    borderRadius: 2,
                    p: 2,
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={1.5}
                  >
                    <CustomText text={`Item ${row + 1}`} fontWeight={600} />

                    {isEditing && (
                      <Tooltip title="Remove this item">
                        <IconButton color="error" onClick={() => setRemovingRow(row)}>
                          <DeleteOutlineIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>

                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>{entryFields(row, 'en')}</Grid>
                    <Grid size={{ xs: 12, md: 6 }}>{entryFields(row, 'ar')}</Grid>
                  </Grid>
                </Box>
              ))}

              {isEditing && (
                <>
                  <Divider />

                  <CustomButton
                    text={'Add Item'}
                    variant={'outlined'}
                    startIcon={<AddIcon />}
                    height={40}
                    fullWidth
                    onClick={addRow}
                  />
                </>
              )}
            </Stack>
          </Grid>
        }
      />

      <CustomDialog
        open={removingRow !== null}
        title={'Remove Item'}
        message={'This removes the item in both languages. It is only stored once you save.'}
        buttons={[
          <CustomButton
            key={'cancel'}
            variant={'outlined'}
            text={'Cancel'}
            textColor={'error'}
            height={35}
            onClick={() => setRemovingRow(null)}
          />,
          <CustomButton
            key={'remove'}
            variant={'filled'}
            color={'error'}
            text={'Remove'}
            height={35}
            onClick={() => {
              if (removingRow !== null) removeRow(removingRow);
              setRemovingRow(null);
            }}
          />,
        ]}
      />
    </Box>
  )
}

export default memo(ContentsDetails)
